import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginSuccess, logout as logoutAction, selectCurrentUser, updateUser } from "../redux/Slice/authSlice";
import { setLoading as setSiteLoading, setSite, setError as setSiteError, selectCategories, selectStoreConfig } from "../redux/Slice/siteSlice";
import { setLoading as setProductsLoading, setProducts, setError as setProductsError, selectProducts } from "../redux/Slice/productSlice";
import { setLoading as setOrdersLoading, setOrders, addOrder, updateOrder, setError as setOrdersError, clearOrders, selectOrders } from "../redux/Slice/orderSlice";
import { errorMessage } from "../services";
import { getConfig } from "../services/storefront";
import { getProducts } from "../services/products";
import { getMe, updateProfile, logout as logoutRequest } from "../services/auth";
import { confirmPayment, createOrder, getOrderById, getOrders, payOrder } from "../services/orders";
import { openRazorpay } from "../utils/razorpayCheckout";
import { createReview, getMyReviews, updateReview, deleteReview } from "../services/reviews";
import useCart from "../hooks/useCart";
import useDebounce from "../utils/useDebounce";
import { PLACEHOLDER_IMG, money, setCurrency, shortText } from "../utils/formatters";
import { applyStoreSeo } from "../utils/seo";
import { applyTheme } from "../utils/theme";
import { celebrate } from "../utils/motion";
import { OFFERS, OWNEZZY_PRIVACY_URL, OWNEZZY_TERMS_URL } from "../utils/ownezzy";
import Home from "../components/Home";
import FashionHome from "../components/FashionHome";
import VariantSheet from "../components/VariantSheet";
import ShopperAlerts from "../components/ShopperAlerts";
import { OwnezzyOfferBar } from "../components/WebsiteOfferFlow";
import ScrollRow from "../components/ScrollRow";
import InstallApp from "../components/InstallApp";
import { AboutPage, BlogPage, ContactPage, FloatingWhatsApp } from "../components/StorePages";
import FloatingCart from "../components/FloatingCart";
import StoreBrand, { storeLogoUrl } from "../components/StoreBrand";
import useStoreType from "../utils/storeTypes";
import { applyFavicon } from "../utils/favicon";
import { captureReferral, trackVisit } from "../utils/referral";
import { initActivityTracker, setActiveStoreSlug, trackActivity, resetScrollMilestones } from "../utils/activityTracker";
import Loader, { Spinner } from "../components/Loader";
import { Offcanvas } from 'react-bootstrap';
import Icon from "../utils/icons";

// Code-split non-critical views and modals so the initial landing page bundle stays light
const PolicyModal = React.lazy(() => import("../components/PolicyModal"));
const AuthModal = React.lazy(() => import("../components/AuthModal"));
const ProductPage = React.lazy(() => import("../components/ProductPage"));
const ProductDetails = React.lazy(() => import("../components/ProductDetails"));
const Cart = React.lazy(() => import("../components/Cart"));
const Checkout = React.lazy(() => import("../components/Checkout"));
const Profile = React.lazy(() => import("../components/Profile"));
const Orders = React.lazy(() => import("../components/Orders"));
const OrderConfirmation = React.lazy(() => import("../components/OrderConfirmation"));
const RateReviewModal = React.lazy(() => import("../components/RateReviewModal"));
const WebsiteOfferFlow = React.lazy(() => import("../components/WebsiteOfferFlow"));
const OtherDemosWidget = React.lazy(() => import("../components/OtherDemosWidget"));
const AiDogChatBot = React.lazy(() => import("../components/AiDogChatBot"));

const pathToView = {
    "/": "Home",
    "/products": "Products",
    "/cart": "Cart",
    "/checkout": "Checkout",
    "/profile": "Profile",
    "/orders": "Orders",
    "/about": "About",
    "/contact": "Contact",
};
const viewToPath = {
    Home: "/",
    Products: "/products",
    Cart: "/cart",
    Checkout: "/checkout",
    Profile: "/profile",
    Orders: "/orders",
    About: "/about",
    Contact: "/contact",
    Blog: "/blog",
};
const PRODUCT_PATH = /^\/product\/(\d+)\/?$/;
// The blog and one post (/blog/<post>): the Blog page reads which from the address
const BLOG_PATH = /^\/blog(\/[^/]+)?\/?$/;
const viewOf = (pathname) => (PRODUCT_PATH.test(pathname) ? "ProductDetail" : BLOG_PATH.test(pathname) ? "Blog" : pathToView[pathname] || "Home");
const isMobile = () => window.matchMedia("(max-width: 767.98px)").matches;
// Pages where the floating cart button shows (not the cart and checkout themselves)
const BROWSING_VIEWS = ["Home", "Products", "ProductDetail"];
// Store types with the modern look and home page (FashionHome.js, fashion.css)
const MODERN_TYPES = [
    "clothing", "gifts", "selfcare", "plants", "aquarium", "fresh", "grocery", "pets",
    "tech", "footwear", "bags", "crafts", "decor",
    "blouses", "uruli", "favors", "resin", "crochet", "pattu", "linens", "tshirts", "acrylic", "beauty", "cosmetics", "kurti", "perfume",
    "toys", "medcare", "dryfruits", "bedbath", "lungi", "organic", "murti", "spices", "snacks", "teacoffee", "gardentools",
    "pickles", "bakery", "jewellery", "sarees", "sweets", "herbal", "healthmix", "dailywear", "tiffin", "bangles",
    "pooja", "electronics", "comics", "coconutoil", "cleaning", "artjewellery", "wallclocks", "mirrors", "candles", "custom"
];

// The storefront. Holds the current view (Home, Products, Cart, ...) in state instead of router routes,
// loads the store config, products, orders and reviews, and passes them down to the components.
function StoreApp() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const user = useSelector(selectCurrentUser);
    const config = useSelector(selectStoreConfig);
    const categories = useSelector(selectCategories);
    const siteState = useSelector((state) => state.site);
    const catalogue = useSelector(selectProducts);
    const productsState = useSelector((state) => state.product);
    const orderState = useSelector(selectOrders);

    const [show, setShow] = useState(false);
    const [categoriesExpanded, setCategoriesExpanded] = useState(false);
    const [categorySearch, setCategorySearch] = useState("");
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [searchBarOpen, setSearchBarOpen] = useState(false); // phones: the search row shows only after tapping the search icon
    const [suggestOpen, setSuggestOpen] = useState(false); // wide screens: the suggestions under the search box
    const [suggestIndex, setSuggestIndex] = useState(-1); // the suggestion picked with the arrow keys
    const [view, setView] = useState(() => viewOf(location.pathname));
    const [category, setCategory] = useState("All");
    const [policy, setPolicy] = useState(null); // "privacyPolicy" | "termsConditions" while its popup is open
    const [query, setQuery] = useState("");
    // A price cap the products page opens with (the home page's "Gifts under ₹499"), null for none
    const [priceCap, setPriceCap] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [toast, setToast] = useState("");
    const [productId, setProductId] = useState(() => {
        const match = location.pathname.match(PRODUCT_PATH);
        return match ? Number(match[1]) : null;
    });
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [auth, setAuth] = useState(null);
    const [pendingCheckout, setPendingCheckout] = useState(false);
    // The login window was opened to buy something (checkout, a gift photo), so it offers checking out as a guest
    const [guestOk, setGuestOk] = useState(false);
    const [authEmail, setAuthEmail] = useState(""); // filled in on the login window (the email typed at checkout)
    const [ratings, setRatings] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [ratingOrder, setRatingOrder] = useState(null);
    const [ratingProduct, setRatingProduct] = useState(null);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const notify = useCallback((message) => {
        setToast(message);
        window.clearTimeout(window.__gnToast);
        window.__gnToast = window.setTimeout(() => setToast(""), 2600);
    }, []);

    const cart = useCart(user, notify);

    // ---- Store configuration and catalogue ----
    const loadStore = useCallback(() => {
        dispatch(setSiteLoading());
        dispatch(setProductsLoading());
        getConfig()
            .then((data) => {
                setCurrency(data.store && data.store.currency); // prices in the store's currency (stores abroad)
                dispatch(setSite(data));
            })
            .catch((err) => dispatch(setSiteError(errorMessage(err))));
        getProducts({ limit: 100 })
            .then((data) => dispatch(setProducts(data.products)))
            .catch((err) => dispatch(setProductsError(errorMessage(err))));
    }, [dispatch]);

    useEffect(() => {
        loadStore();
        captureReferral();
        initActivityTracker(() => (config ? config.slug : ""));
    }, [loadStore]);

    const storeSlug = config ? config.slug : "";
    useEffect(() => {
        trackVisit(storeSlug);
        if (storeSlug) setActiveStoreSlug(storeSlug);
    }, [storeSlug]);

    useEffect(() => {
        resetScrollMilestones(location.pathname);
        trackActivity("navigation", `Navigated to ${view} (${location.pathname})`, { view, path: location.pathname });
    }, [view, location.pathname]);

    const type = useStoreType();
    const themeColor = config ? config.color : "";
    // A design's gradient applies only while the owner keeps its colour; a colour they picked is used as it is
    const themeGradient = type.gradient && String(themeColor).toLowerCase() === type.color ? type.gradient : null;
    useEffect(() => {
        applyTheme(themeColor, themeGradient);
    }, [themeColor, themeGradient]);

    // Clothing stores' fonts (fashion.css): an editorial serif for headings and a clean sans for text
    const fashionFonts = Boolean(config && MODERN_TYPES.includes(config.template));
    useEffect(() => {
        if (!fashionFonts || document.getElementById("fx-fonts")) return;
        const link = document.createElement("link");
        link.id = "fx-fonts";
        link.rel = "stylesheet";
        link.href = "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500&family=Jost:wght@400;500;600&display=swap";
        document.head.appendChild(link);
    }, [fashionFonts]);

    // The browser-tab icon: the store's own, Ownezzy's on demo stores, or the store type's emoji
    useEffect(() => {
        if (config) applyFavicon(config, type);
    }, [config, type]);

    // Every store runs this same build, so the title, description and search tags come from the loaded store
    useEffect(() => {
        applyStoreSeo(config, categories);
    }, [config, categories]);

    // Stock changes after every order, so refresh the catalogue quietly
    const refreshCatalogue = useCallback(() => {
        getProducts({ limit: 100 })
            .then((data) => dispatch(setProducts(data.products)))
            .catch(() => {});
    }, [dispatch]);

    // ---- Session ----
    // A saved token is checked with the server on load; if it is no longer valid the 401 handler logs us out.
    useEffect(() => {
        if (!user) return;
        getMe()
            .then((fresh) => dispatch(updateUser(fresh)))
            .catch(() => {});
        // only once, when the page opens
    }, []);

    // The header gets a shadow once the page scrolls under it (fashion.css)
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // A part of the page that needs the shopper signed in (uploading a photo for a gift) asks for the login window
    useEffect(() => {
        const askLogin = (event) => {
            setGuestOk(true);
            setAuth("login");
            if (event.detail) notify(event.detail);
        };
        window.addEventListener("gn:login", askLogin);
        return () => window.removeEventListener("gn:login", askLogin);
    }, [notify]);

    useEffect(() => {
        const onUnauthorized = () => {
            dispatch(logoutAction());
            cart.clear();
            notify("Your session ended. Please log in again.");
        };
        window.addEventListener("gn:unauthorized", onUnauthorized);
        return () => window.removeEventListener("gn:unauthorized", onUnauthorized);
    }, [dispatch, notify, cart.clear]);

    // ---- Orders and reviews belong to the signed-in shopper ----
    const loadOrders = useCallback(
        (page = 1) => {
            dispatch(setOrdersLoading());
            getOrders({ page, limit: 10 })
                .then((data) => dispatch(setOrders({ orders: data.orders, page: data.page, pages: data.pages, total: data.total })))
                .catch((err) => dispatch(setOrdersError(errorMessage(err))));
        },
        [dispatch]
    );

    const userId = user ? user.id : null;
    useEffect(() => {
        if (!userId) {
            dispatch(clearOrders());
            setRatings([]);
            return;
        }
        loadOrders(1);
        getMyReviews()
            .then(setRatings)
            .catch((err) => {
                // a 401 means the session ended, which the "gn:unauthorized" handler already announces
                if (!(err.response && err.response.status === 401)) notify(errorMessage(err));
            });
    }, [userId, dispatch, loadOrders, notify]);

    // ---- Navigation ----
    const navActive = (name) => view === "Products" && category === name;
    const navigateFromMenu = (target, selectedCategory) => {
        handleClose();
        go(target, selectedCategory);
    };

    // The products page with a category, a search (an occasion: products tagged with it) and a price cap
    // An occasion opens as a search; it is cleared again when the shopper leaves the products page, so the home page
    // does not stay on "results for Diwali"
    const browseSearch = useRef("");
    const browse = ({ category: c = "All", search = "", maxPrice = null } = {}) => {
        go("Products", c);
        setQuery(search);
        browseSearch.current = search;
        setPriceCap(maxPrice); // after go(), which clears it
    };

    const go = (v, c) => {
        setPriceCap(null); // only "under ₹…" from the home page opens the products page with a price cap
        if (v !== "Products" && browseSearch.current) {
            if (query === browseSearch.current) setQuery("");
            browseSearch.current = "";
        }
        setView(v);
        if (v !== "ProductDetail") {
            setSelectedProduct(null);
            setProductId(null);
        }
        navigate(viewToPath[v] || "/");
        if (c) setCategory(c);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // An order update (the bell, or a tapped notification at /orders?order=...): My orders with that order open
    const openOrder = async (orderId) => {
        go("Orders");
        try {
            const order = await getOrderById(orderId);
            dispatch(updateOrder(order));
            setSelectedOrder(order);
        } catch (err) {
            notify(errorMessage(err));
        }
    };
    // An order update arrived (the bell): the order list and an open order show its new status at once
    const refreshOrderFromAlert = async (orderId) => {
        try {
            const order = await getOrderById(orderId);
            dispatch(updateOrder(order));
            setSelectedOrder((current) => (current && current.id === order.id ? order : current));
        } catch {
            // the list catches up the next time it loads
        }
    };
    const openOrderRef = useRef(openOrder);
    openOrderRef.current = openOrder;
    const linkedOrder = new URLSearchParams(location.search).get("order");
    useEffect(() => {
        if (userId && linkedOrder) openOrderRef.current(linkedOrder);
    }, [userId, linkedOrder]);

    const openProduct = (product) => {
        if (!product) return;
        setSelectedProduct(product);
        setProductId(product.id);
        setView("ProductDetail");
        navigate(`/product/${product.id}`);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    useEffect(() => {
        const productMatch = location.pathname.match(PRODUCT_PATH);
        if (productMatch) {
            setProductId(Number(productMatch[1]));
            setView("ProductDetail");
            return;
        }
        setSelectedProduct(null);
        setProductId(null);
        setView(viewOf(location.pathname));
    }, [location.pathname]);

    // ---- Search (server side, after the shopper stops typing) ----
    const searchTerm = useDebounce(query, 300).trim();
    useEffect(() => {
        if (!searchTerm) {
            setSearchResults([]);
            setSearching(false);
            return undefined;
        }
        let ignore = false;
        setSearching(true);
        getProducts({ search: searchTerm, limit: 24 })
            .then((data) => {
                if (!ignore) setSearchResults(data.products);
            })
            .catch((err) => {
                if (!ignore) notify(errorMessage(err));
            })
            .finally(() => {
                if (!ignore) setSearching(false);
            });
        return () => {
            ignore = true;
        };
    }, [searchTerm, notify]);

    // With nothing typed the overlay suggests the first products of the store
    const mobileSearchResults = useMemo(
        () => (query.trim() ? searchResults : catalogue).slice(0, 8),
        [query, searchResults, catalogue]
    );

    const chooseMobileSearchResult = (product) => {
        // Open the product listing page with the selected item as the active search.
        setQuery(product.name);
        setCategory("All");
        setMobileSearchOpen(false);
        setSuggestOpen(false);
        setSelectedProduct(null);
        setView("Products");
        navigate("/products", { state: { searchQuery: product.name, selectedProductId: product.id } });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const submitSearch = (event) => {
        // Wide screens: the arrow keys move through the suggestions, Escape closes them
        if (!isMobile() && suggestOpen && mobileSearchResults.length && ["ArrowDown", "ArrowUp"].includes(event?.key)) {
            event.preventDefault();
            // -1 (the box itself), then each suggestion, then round again
            const states = mobileSearchResults.length + 1;
            const step = event.key === "ArrowDown" ? 1 : -1;
            setSuggestIndex((i) => ((i + 1 + step + states) % states) - 1);
            return;
        }
        if (event?.key === "Escape") { setSuggestOpen(false); return; }
        if (event?.key && event.key !== "Enter") return;
        if (event?.preventDefault) event.preventDefault();
        if (isMobile()) {
            setMobileSearchOpen(true);
            return;
        }
        if (event?.key === "Enter" && suggestOpen && mobileSearchResults[suggestIndex]) {
            chooseMobileSearchResult(mobileSearchResults[suggestIndex]);
            return;
        }
        setSuggestOpen(false);
        setCategory("All");
        setSelectedProduct(null);
        setView("Products");
        navigate("/products");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ---- Account ----
    const startCheckout = () => {
        if (!cart.items.length) return notify("Your cart is empty");
        // No sign-in first: a guest gives their email on the checkout page and confirms it when placing the order
        go("Checkout");
    };

    const logout = async () => {
        try {
            await logoutRequest();
        } catch (err) {
            // Even if the server can't be reached, this device is signed out below
        }
        dispatch(logoutAction());
        cart.clear();
        notify("Logged out successfully");
        go("Home");
    };

    const saveProfile = async (data) => {
        try {
            const { name, email, phone, address, city, pincode } = data;
            const updated = await updateProfile({ name, email, phone, address, city, pincode });
            dispatch(updateUser(updated));
            notify("Profile updated");
            return true;
        } catch (err) {
            notify(errorMessage(err));
            return false;
        }
    };

    // ---- Orders ----
    // The server prices the order and checks stock. Only product ids and quantities are sent.
    // gifts: the gift options picked at checkout ({ giftWrap, giftCard: { message }, deliveryDate }, gift stores)
    // guest: { token, user } from a guest's email code. The order is placed with it first and the guest is signed in on
    // this browser only once the cart is emptied, so the cart sync at sign-in cannot bring the ordered items back.
    const placeOrder = async ({ note, ...details }, payment, couponCode, gifts = {}, guest = null) => {
        try {
            const { order, customer, checkout, payError } = await createOrder({
                items: cart.items.map((item) => ({ productId: item.id, qty: item.qty, size: item.size, color: item.color, text: item.text, photos: item.photos })),
                ...gifts,
                customer: details,
                note,
                payment,
                ...(couponCode ? { couponCode } : {}),
            }, guest?.token);
            if (guest) {
                cart.clear();
                dispatch(loginSuccess({ token: guest.token, user: customer || guest.user }));
            }
            dispatch(addOrder(order));
            // A gift on its way: a little celebration (utils/motion.js)
            if (config.template === "gifts") celebrate();
            if (customer) dispatch(updateUser(customer)); // the address is now saved in their profile
            cart.clear();
            if (payError) notify(payError);
            refreshCatalogue();
            setSelectedOrder(order);
            go("Orders");
            // Paying through the store's Razorpay account: its UPI popup opens over the order straight away
            if (checkout) openPayment(order, checkout);
        } catch (err) {
            const message = errorMessage(err);
            notify(message);
            throw new Error(message);
        }
    };

    // Asked every few seconds while a payment screen is open. The order changes when the gateway (Razorpay)
    // confirms the payment.
    const refreshOrder = async (order) => {
        try {
            const updated = await getOrderById(order.id);
            if (updated.paymentStatus === order.paymentStatus) return;
            dispatch(updateOrder(updated));
            setSelectedOrder((current) => (current && current.id === updated.id ? updated : current));
            if (updated.paymentStatus === "paid") notify(`Payment received for order ${updated.id}. Thank you!`);
        } catch {
            // a missed check is retried on the next one
        }
    };

    const showOrder = (order) => {
        dispatch(updateOrder(order));
        setSelectedOrder((current) => (current && current.id === order.id ? order : current));
    };

    // Razorpay's UPI popup for a gateway order. Its signed result is confirmed with the server; if the shopper closes
    // it, the order waits with "Pay now" (and is still marked paid if the payment finishes in their UPI app).
    const openPayment = (order, checkout) =>
        openRazorpay(checkout, {
            shopper: { name: order.customer.name, phone: order.customer.phone, email: user?.email },
            color: /^#[0-9a-f]{6}$/i.test(config.color || "") ? config.color : undefined, // Razorpay takes a hex colour only
            onPaid: async (result) => {
                try {
                    const updated = await confirmPayment(order.id, result);
                    showOrder(updated);
                    if (updated.paymentStatus === "paid") notify(`Payment received for order ${updated.id}. Thank you!`);
                } catch (err) {
                    notify(errorMessage(err)); // the background checks still pick the payment up
                }
            },
            onClose: () => {},
        }).catch((err) => notify(err.message));

    // Pay a gateway order (again). If it was paid meanwhile, the order just updates.
    const payOnline = async (order) => {
        try {
            const { order: latest, checkout } = await payOrder(order.id);
            showOrder(latest);
            if (checkout) await openPayment(latest, checkout);
        } catch (err) {
            throw new Error(errorMessage(err));
        }
    };

    // ---- Reviews ----
    const openRating = (order, product = null) => {
        setRatingOrder(order);
        setRatingProduct(product);
    };

    const submitReview = async (review) => {
        try {
            const created = await createReview(review);
            setRatings((current) => [created, ...current]);
            notify("Thanks! Your review has been submitted.");
        } catch (err) {
            throw new Error(errorMessage(err));
        }
    };

    const editReview = async (review, text) => {
        try {
            const updated = await updateReview(review.id, { text });
            setRatings((current) => current.map((item) => (item.id === updated.id ? updated : item)));
            notify("Review updated");
        } catch (err) {
            notify(errorMessage(err));
        }
    };

    const removeReview = async (review) => {
        try {
            await deleteReview(review.id);
            setRatings((current) => current.filter((item) => item.id !== review.id));
            notify("Review deleted");
        } catch (err) {
            notify(errorMessage(err));
        }
    };

    // ---- Waiting for / failed to load the store ----
    if (!config) {
        return (
            <div className="app storeStatus">
                {siteState.error ? (
                    <>
                        <h2>We couldn't open this store</h2>
                        <p>{siteState.error}</p>
                        <button className="green" onClick={loadStore}>Try again</button>
                    </>
                ) : (
                    <Loader label="Opening the store" page />
                )}
            </div>
        );
    }

    const storeName = config.name;
    const loginPrompt = () => {
        if (user) return go("Profile");
        setGuestOk(false);
        setAuth("login");
    };
    const showFloatingCart = cart.count > 0 && BROWSING_VIEWS.includes(view);
    // Clothing and gift stores get the modern look (fashion.css, coloured per type by theme-<type>) and its home page
    const fashion = MODERN_TYPES.includes(config.template);
    // The search box hint: Ownezzy's demo stores show their type's sample wording ("Search plants, pots, seeds & more");
    // a seller's own store shows a plain hint, so it never names products the store doesn't sell
    const searchHint = config.isDemo || config.showOwnezzyOffer ? type.searchPlaceholder : "Search products";

    return (
        <div className={["app", showFloatingCart && "hasFloatingCart", fashion && "theme-fashion", fashion && `theme-${config.template}`, fashion && config.layout === "v2" && "layout-v2"].filter(Boolean).join(" ")}>
            {config.showOwnezzyOffer && <OwnezzyOfferBar />}
            {config.announcement && <div className="offer">{config.announcement}</div>}
            <header className={scrolled ? "isScrolled" : ""}>
                <div className={`nav px-2 px-md-3 ${searchBarOpen ? "searchBarOpen" : ""}`}>
                    <div className="first-col">
                        <button className="brand" onClick={() => go("Home")}>
                            <StoreBrand config={config} type={type} />
                        </button>
                        {/* The store's own pages, beside the logo (on phones they are in the side menu) */}
                        <nav className="headerPages" aria-label="Store pages">
                            <button className={view === "About" ? "active" : ""} aria-current={view === "About" ? "page" : undefined} onClick={() => go("About")}>{(config.aboutPage && config.aboutPage.title) || "About us"}</button>
                            <button className={view === "Blog" ? "active" : ""} aria-current={view === "Blog" ? "page" : undefined} onClick={() => go("Blog")}>Blog</button>
                            <button className={view === "Contact" ? "active" : ""} aria-current={view === "Contact" ? "page" : undefined} onClick={() => go("Contact")}>Contact us</button>
                        </nav>
                    </div>
                    <div className="second-col search">
                        <span className="search-top-icon"><Icon.Search className="icon-view-inpt-search" /></span>
                        <input
                            value={query}
                            onFocus={() => { if (isMobile()) setMobileSearchOpen(true); else setSuggestOpen(true); }}
                            onClick={() => { if (isMobile()) setMobileSearchOpen(true); else setSuggestOpen(true); }}
                            onBlur={() => setSuggestOpen(false)}
                            onChange={(e) => { setQuery(e.target.value); setSuggestIndex(-1); if (!isMobile()) setSuggestOpen(true); }}
                            onKeyDown={submitSearch}
                            placeholder={searchHint}
                            aria-label={searchHint}
                            role="combobox"
                            aria-expanded={suggestOpen}
                            aria-controls="search-suggest"
                            aria-autocomplete="list"
                        />
                        {/* Wide screens: products as the shopper types (popular picks with nothing typed). A mouse
                            press keeps the focus in the box, so a click on a suggestion is not lost to the blur. */}
                        {suggestOpen && (
                            <div className="search-suggest" id="search-suggest" role="listbox" aria-label="Search suggestions" onMouseDown={(e) => e.preventDefault()}>
                                <div className="search-suggest-title">
                                    <span>{query.trim() ? `Results for “${query.trim()}”` : "Popular picks"}</span>
                                    {searching && <small><Spinner /> Searching</small>}
                                </div>
                                {mobileSearchResults.length > 0 ? mobileSearchResults.map((product, i) => (
                                    <button type="button" role="option" aria-selected={i === suggestIndex} className={`search-suggest-item ${i === suggestIndex ? "is-active" : ""}`} key={product.id}
                                        onMouseEnter={() => setSuggestIndex(i)} onClick={() => chooseMobileSearchResult(product)}>
                                        <img src={product.img || PLACEHOLDER_IMG} alt="" loading="lazy" />
                                        <span className="search-suggest-copy"><b>{product.name}</b><small>{product.cat}</small></span>
                                        <strong>{money(product.price)}</strong>
                                    </button>
                                )) : !searching && (
                                    <p className="search-suggest-empty">No matching products. Try a {type.item} name or a category.</p>
                                )}
                                {query.trim() && (
                                    <button type="button" className="search-suggest-all" onClick={() => submitSearch()}>
                                        <Icon.Search /> See all results for “{query.trim()}”
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="third-col d-flex flex-wrap align-items-center">
                        <button
                            type="button"
                            className={`mobile-search-toggle ${searchBarOpen ? "active" : ""}`}
                            onClick={() => setSearchBarOpen((open) => !open)}
                            aria-label={searchBarOpen ? "Hide search" : "Search products"}
                            aria-expanded={searchBarOpen}
                        >
                            {searchBarOpen ? <Icon.XLg size={19} /> : <Icon.Search size={20} />}
                        </button>
                        <ShopperAlerts userId={userId} notify={notify} onOpenOrder={openOrder} onOrderChanged={refreshOrderFromAlert} />
                        <button className="profileNav p-0" onClick={loginPrompt}>
                            {user ? `Hi, ${user.name?.split(" ")[0] || "there"}` : "Login"}
                        </button>
                        <button className="cart-button btn border-0 p-0 position-relative ms-3" onClick={() => go("Cart")}>
                            <Icon.Cart2 size="22" /> <span className="icon-cart-bages" key={cart.count}>{cart.count}</span>
                        </button>
                        <button type="button" className="mobile-menu-trigger" aria-label="Open navigation menu" onClick={handleShow}>
                            <Icon.List size={25} />
                        </button>
                    </div>
                </div>
                <ScrollRow className="nav2" label="Categories" autoScroll>
                    {/* The category being shown on the products page is highlighted */}
                    {config.hasOffers && <button className={`nav2-offers ${navActive(OFFERS) ? "active" : ""}`} aria-current={navActive(OFFERS) ? "page" : undefined} onClick={() => go("Products", OFFERS)}>🏷️ {config.offersTitle}</button>}
                    {categories.map((name) => (
                        <button key={name} className={navActive(name) ? "active" : ""} aria-current={navActive(name) ? "page" : undefined} onClick={() => go("Products", name)}>
                            {name}
                        </button>
                    ))}
                </ScrollRow>
            </header>
            {mobileSearchOpen && (
                <div className="mobile-search-overlay" role="dialog" aria-modal="true" aria-label="Search products">
                    <div className="mobile-search-header">
                        <button type="button" className="mobile-search-back" onClick={() => setMobileSearchOpen(false)} aria-label="Close search"><Icon.ArrowLeft /></button>
                        <div className="mobile-search-input-wrap">
                            <Icon.Search />
                            <input
                                autoFocus
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === "Escape") setMobileSearchOpen(false);
                                    else if (event.key === "Enter") {
                                        event.preventDefault();
                                        setCategory("All");
                                        setMobileSearchOpen(false);
                                        setSelectedProduct(null);
                                        setView("Products");
                                        navigate("/products");
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }
                                }}
                                placeholder={searchHint}
                                aria-label="Search products"
                            />
                            {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search"><Icon.X /></button>}
                        </div>
                    </div>
                    <div className="mobile-search-content">
                        <div className="mobile-search-title"><div><span>{query.trim() ? "SEARCH RESULTS" : "POPULAR PICKS"}</span><h2>{query.trim() ? `Results for “${query.trim()}”` : type.searchPrompt || "What are you growing today?"}</h2></div><small>{searching ? <><Spinner /> Searching</> : `${mobileSearchResults.length} items`}</small></div>
                        {mobileSearchResults.length > 0 ? (
                            <div className="mobile-search-results">
                                {mobileSearchResults.map((product) => (
                                    <button type="button" className="mobile-search-result" key={product.id} onClick={() => chooseMobileSearchResult(product)}>
                                        <img src={product.img || PLACEHOLDER_IMG} alt={product.name} loading="lazy" />
                                        <span className="mobile-search-result-copy"><b>{product.name}</b><small>{product.cat} · {shortText(product.desc)}</small><strong>{money(product.price)}</strong></span>
                                        <Icon.ChevronRight className="mobile-search-result-arrow" />
                                    </button>
                                ))}
                            </div>
                        ) : searching ? (
                            <Loader label={`Searching ${type.items}`} />
                        ) : (
                            <div className="mobile-search-empty"><span><Icon.Search /></span><h3>No matching products</h3><p>Try a {type.item} name or a category such as “{categories[0] || "Offers"}”.</p><button onClick={() => setQuery("")}>Browse popular picks</button></div>
                        )}
                    </div>
                </div>
            )}
            <Offcanvas show={show} onHide={handleClose} placement="end" className="greennest-offcanvas">
                <Offcanvas.Header closeButton className="mobile-drawer-header">
                    <Offcanvas.Title>
                        <span className="drawer-brand-icon">{storeLogoUrl(config) ? <img src={storeLogoUrl(config)} alt="" /> : type.icon}</span>
                        <span className="drawer-brand-copy">{storeName}<small>{type.tagline}</small></span>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="mobile-drawer-body">
                  <div className="drawer-scroll-area">
                    <div className="drawer-welcome">
                        <div className="drawer-avatar">{user ? (user.name || "P").charAt(0).toUpperCase() : type.icon}</div>
                        <div><strong>{user ? `Hello, ${user.name?.split(" ")[0] || "there"}!` : `Welcome to ${storeName}`}</strong>
                            <span>{user ? (user.email || type.accountLine) : type.guestLine}</span></div>
                    </div>
                    <div className="drawer-section-label">EXPLORE</div>
                    <nav className="drawer-links" aria-label="Mobile navigation">
                        <button onClick={() => navigateFromMenu("Home")}><span className="drawer-link-icon"><Icon.HouseDoor /></span><span>Home</span><Icon.ChevronRight className="drawer-chevron" /></button>
                        <button className="drawer-category-toggle" aria-expanded={categoriesExpanded} onClick={() => setCategoriesExpanded((expanded) => !expanded)}><span className="drawer-link-icon"><Icon.Leaf /></span><span>Shop by category</span><span className="drawer-category-total">{categories.length}</span>{categoriesExpanded ? <Icon.ChevronUp className="drawer-chevron" /> : <Icon.ChevronDown className="drawer-chevron" />}</button>
                        {categoriesExpanded && <div className="drawer-category-panel">
                            {categories.length > 6 && <label className="drawer-category-search"><Icon.Search /><input value={categorySearch} onChange={(event) => setCategorySearch(event.target.value)} placeholder="Search categories" aria-label="Search categories" /></label>}
                            {categories.filter((item) => item.toLowerCase().includes(categorySearch.toLowerCase())).map((item) => <button className="drawer-category-item" key={item} onClick={() => { setCategorySearch(""); navigateFromMenu("Products", item); }}><span>{item}</span><Icon.ChevronRight /></button>)}
                            {categories.filter((item) => item.toLowerCase().includes(categorySearch.toLowerCase())).length === 0 && <p className="drawer-no-categories">No matching categories found.</p>}
                        </div>}
                        <button onClick={() => navigateFromMenu("Products", "All")}><span className="drawer-link-icon"><Icon.Tags /></span><span>All products</span><Icon.ChevronRight className="drawer-chevron" /></button>
                        <button onClick={() => navigateFromMenu("About")}><span className="drawer-link-icon"><Icon.InfoCircle /></span><span>{(config.aboutPage && config.aboutPage.title) || "About us"}</span><Icon.ChevronRight className="drawer-chevron" /></button>
                        <button onClick={() => navigateFromMenu("Blog")}><span className="drawer-link-icon"><Icon.JournalText /></span><span>Blog</span><Icon.ChevronRight className="drawer-chevron" /></button>
                        <button onClick={() => navigateFromMenu("Contact")}><span className="drawer-link-icon"><Icon.Telephone /></span><span>Contact us</span><Icon.ChevronRight className="drawer-chevron" /></button>
                    </nav>
                    <div className="drawer-section-label">YOUR ACCOUNT</div>
                    <nav className="drawer-links" aria-label="Account navigation">
                        <button onClick={() => { handleClose(); loginPrompt(); }}><span className="drawer-link-icon"><Icon.PersonCircle /></span><span>{user ? "My profile" : "Login / Sign up"}</span><Icon.ChevronRight className="drawer-chevron" /></button>
                        <button onClick={() => navigateFromMenu("Orders")}><span className="drawer-link-icon"><Icon.BagCheck /></span><span>My orders</span><Icon.ChevronRight className="drawer-chevron" /></button>
                        <button onClick={() => navigateFromMenu("Cart")}><span className="drawer-link-icon"><Icon.Cart3 /></span><span>Shopping cart</span><span className="drawer-count">{cart.count}</span><Icon.ChevronRight className="drawer-chevron" /></button>
                    </nav>
                  </div>
                    <div className="drawer-bottom">
                        <InstallApp className="drawer-install" storeName={storeName} />
                        {user ? <button className="drawer-logout" onClick={() => { handleClose(); logout(); }}><Icon.BoxArrowRight /> Logout</button> : <button className="drawer-login" onClick={() => { handleClose(); loginPrompt(); }}><Icon.BoxArrowInRight /> Login to your account</button>}
                        <p>{type.footerNote}</p>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
            {toast && <div className="toast">✓ {toast}</div>}
            {/* key: each page enters with a short fade (fashion.css .viewEnter) */}
            <main className="viewEnter" key={view === "ProductDetail" ? `p${productId}` : view}>
                {productsState.error && (
                    <div className="noResults" role="alert">
                        {productsState.error} <button className="textBtn" onClick={loadStore}>Try again</button>
                    </div>
                )}
                <React.Suspense fallback={<Loader />}>
                    {view === "Home" && (fashion ? (
                        <FashionHome go={go} browse={browse} query={query} filtered={searchResults} add={cart.add} onSelect={openProduct} cart={cart.items} updateQty={cart.updateQty} />
                    ) : (
                        <Home go={go} query={query} filtered={searchResults} add={cart.add} onSelect={openProduct} cart={cart.items} updateQty={cart.updateQty} />
                    ))}
                    {view === "Products" && (
                        <ProductPage
                            key={`price-${priceCap}`}
                            startPrice={priceCap}
                            category={category}
                            setCategory={setCategory}
                            query={query}
                            setQuery={setQuery}
                            add={cart.add}
                            onSelect={openProduct}
                            cart={cart.items}
                            updateQty={cart.updateQty}
                        />
                    )}
                    {view === "About" && <AboutPage />}
                    {view === "Blog" && <BlogPage />}
                    {view === "Contact" && <ContactPage />}
                    {view === "ProductDetail" && (
                        <ProductDetails key={productId} productId={productId} initial={selectedProduct} add={cart.add} onSelect={openProduct} go={go} cart={cart.items} updateQty={cart.updateQty} />
                    )}
                    {view === "Cart" && (
                        <Cart
                            cart={cart.items}
                            updateQty={cart.updateQty}
                            remove={cart.remove}
                            go={go}
                            checkout={startCheckout}
                        />
                    )}
                    {view === "Checkout" && (
                        <Checkout
                            user={user}
                            cart={cart.items}
                            placeOrder={placeOrder}
                            go={go}
                            onSignIn={(email) => { setAuthEmail(email || ""); setPendingCheckout(true); setGuestOk(false); setAuth("login"); }}
                            onPolicy={setPolicy}
                        />
                    )}
                    {view === "Profile" && (
                        <Profile
                            user={user}
                            saveProfile={saveProfile}
                            logout={logout}
                            orders={orderState.orders}
                            ratings={ratings}
                            go={go}
                            setSelectedOrder={setSelectedOrder}
                            onRate={openRating}
                            onEditReview={editReview}
                            onDeleteReview={removeReview}
                        />
                    )}
                    {view === "Orders" && (
                        <Orders
                            orders={orderState.orders}
                            hasMore={orderState.page < orderState.pages}
                            loading={orderState.loading}
                            error={orderState.error}
                            loadMore={() => loadOrders(orderState.page + 1)}
                            go={go}
                            setSelectedOrder={setSelectedOrder}
                            onRate={openRating}
                            notify={notify}
                        />
                    )}
                </React.Suspense>
            </main>
            <React.Suspense fallback={null}>
                {selectedOrder && (
                    <OrderConfirmation
                        order={selectedOrder}
                        storeName={storeName}
                        refreshOrder={refreshOrder}
                        payOnline={payOnline}
                        close={() => setSelectedOrder(null)}
                        go={go}
                    />
                )}
                {ratingOrder && (
                    <RateReviewModal
                        order={ratingOrder}
                        initialProduct={ratingProduct}
                        close={() => {
                            setRatingOrder(null);
                            setRatingProduct(null);
                        }}
                        submitReview={submitReview}
                    />
                )}
            </React.Suspense>
            <footer>
                <div>
                    <b className="footerBrand"><StoreBrand config={config} type={type} /></b>
                    <p>{config.about}</p>
                    <div className="socials">
                        <span>◎</span>
                        <span>f</span>
                        <span>▶</span>
                        <span>𝕏</span>
                    </div>
                </div>
                <div>
                    <h3 className="title-footer">Shop</h3>
                    <ul className="ftr-ul-menu">
                        {categories.slice(0, 4).map((name) => (
                            <li className="wgt-ftr-menu" key={name}>
                                <button onClick={() => go("Products", name)}>{name}</button>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h3 className="title-footer">{type.infoTitle}</h3>
                    <ul className="ftr-ul-menu">
                        <li className="wgt-ftr-menu"><button onClick={() => go("About")}>{(config.aboutPage && config.aboutPage.title) || "About us"}</button></li>
                        <li className="wgt-ftr-menu"><button onClick={() => go("Blog")}>Blog</button></li>
                        {type.infoLinks.map((link) => (
                            <li className="wgt-ftr-menu" key={link}>
                                <button onClick={link === "Contact" ? () => go("Contact") : undefined}>{link === "Contact" ? "Contact us" : link}</button>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <h3 className="title-footer">Account</h3>
                    <ul className="ftr-ul-menu">
                        <li className="wgt-ftr-menu">
                            <button onClick={loginPrompt}>My profile</button>
                        </li>
                        <li className="wgt-ftr-menu">
                            <button onClick={() => go("Orders")}>Orders</button>
                        </li>
                        <li className="wgt-ftr-menu">
                            <button onClick={() => go("Cart")}>Cart</button>
                        </li>
                        <li className="wgt-ftr-menu">
                            {user && <button onClick={logout}>Logout</button>}
                        </li>
                        <li className="wgt-ftr-menu"><InstallApp className="footer-install" storeName={storeName} /></li>
                    </ul>
                </div>
                {/* The store's own policies (website editor -> Policies) and, on Ownezzy's demo and trial stores,
                    Ownezzy's platform Terms and Privacy Policy */}
                <div className="footer-legal">
                    {config.privacyPolicy && <button onClick={() => setPolicy("privacyPolicy")}>Privacy Policy</button>}
                    {config.termsConditions && <button onClick={() => setPolicy("termsConditions")}>Terms &amp; Conditions</button>}
                    {(config.showOwnezzyOffer || config.showOwnezzyCredit) && (
                        <>
                            <a href={OWNEZZY_TERMS_URL} target="_blank" rel="noopener">Ownezzy Terms of Service</a>
                            <a href={OWNEZZY_PRIVACY_URL} target="_blank" rel="noopener">Ownezzy Privacy Policy</a>
                        </>
                    )}
                    {config.showOwnezzyOffer && <span>This is a demo store by Ownezzy. Products and orders here are samples.</span>}
                </div>
                {/* Ownezzy branding: the backend decides (demo store / free trial / paid), see storefrontConfig.js */}
                {/* ?ref= names the store the visitor came from, so later sign-ups can be traced back to it */}
                {config.showOwnezzyCredit && (
                    <a
                        className="ownezzy-credit"
                        href={`https://ownezzy.com/?ref=${encodeURIComponent(config.slug)}`}
                        target="_blank"
                        rel="noopener"
                    >
                        Website by <span className="ownezzy-credit-spark" aria-hidden="true">✦</span> <b>Ownezzy</b> · Get your own free online store
                    </a>
                )}
            </footer>
            {showFloatingCart && <FloatingCart items={cart.items} onOpen={() => go("Cart")} />}
            {/* Size and colour picker for products added from a card (utils/variants.js) */}
            <VariantSheet add={cart.add} onSelect={openProduct} />
            {/* Shoppers chat with the shop on WhatsApp from any page (raised above the demo stores' sign-up button) */}
            <FloatingWhatsApp raised={Boolean(config.showOwnezzyOffer)} />
            <React.Suspense fallback={null}>
                {config.showOwnezzyOffer && <WebsiteOfferFlow />}
                {Boolean(config?.isDemo || config?.showOwnezzyOffer) && <OtherDemosWidget />}
                {Boolean(config?.isDemo || config?.showOwnezzyOffer) && <AiDogChatBot />}
                {policy && (
                    <PolicyModal
                        title={policy === "privacyPolicy" ? "Privacy Policy" : "Terms & Conditions"}
                        text={config[policy]}
                        onClose={() => setPolicy(null)}
                    />
                )}
                {auth && (
                    <AuthModal
                        mode={auth}
                        setMode={setAuth}
                        atCheckout={guestOk}
                        initialEmail={authEmail}
                        close={() => {
                            setAuth(null);
                            setAuthEmail("");
                            setPendingCheckout(false);
                        }}
                        onLogin={(shopper) => {
                            setAuth(null);
                            notify(shopper?.guest ? "Email confirmed" : "Welcome back!");
                            // Stay on the page the shopper was on; only a login asked for at checkout moves on
                            if (pendingCheckout) {
                                setPendingCheckout(false);
                                go("Checkout");
                            }
                        }}
                    />
                )}
            </React.Suspense>
        </div>
    );
}

export default StoreApp;
